import { broker } from '#~/agent/broker.js';
import type { Proxy } from '#~/agent/types.js';
import { ElectronAPI } from '#~/electron/api.js';
import { logger } from '#~/logger/index.js';
import type { T_Maybe } from '@tryiris-ai/js-types';
import {
  ViscaBridgeCommand,
  ViscaBridgeInquiryResponse,
  ViscaBridgeRequest
} from '@tryiris-ai/common.protojs/joystick/v1/joystick';
import { Repeater } from './movement.js';
import { fetchModelConfig } from '#~/api/agent/registry.js';
import { EventEmitter } from 'events';
import { locateProxy } from '#~/agent/utils.js';
import {
  DeviceRegistryCommandKey,
  DeviceRegistryCommand
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import { handlePresetCommand } from './presets.js';
import { buildSettingFromCommand } from './settings.js';
import { processBlockInquiry, processInquiry } from './inquiry.js';
import type { Device } from '@studio/models/device';
import { isElectronApp } from '#~/electron/utils.js';
import {
  isDesktopOnlyFeatureAvailable,
  reportDesktopOnlyFeatureGate,
  resolveFeatureGateClient
} from '#~/analytics/domains/feature-gate.js';
import {
  DESKTOP_ONLY_FEATURE,
  type EnvelopeClient
} from '#~/analytics/constants/event-properties.js';
import { saveControllers, CONTROLLER_SCHEME } from './restore.js';

// Renderer must fail BEFORE Electron main's INIT_DEADLINE_MS (15s) so we
// never enter the "main says ready, but renderer already destroyed state"
// gotcha. 12s leaves 3s of headroom for IPC round-trip. See ENG-3756 spec
// constants block; mirrors the iris-auth waitForTokenServer (ENG-3708)
// readiness-gate precedent.
//
// See knowledge: iris-auth-token-server-readiness-gate (ai.knowledge/
// platform/electron-vite-app) — direct precedent for this pattern.
const RENDERER_TIMEOUT_MS = 12_000;

export interface IPControllerInfo {
  name: string;
  port: number;
  localIP: string;
  controlledDeviceID?: string;
  generation?: number;
  // Electron main reports whether a usable bridge process currently exists
  // for this saved controller. Absent on the browser surface, where there
  // is no runtime to report on.
  available?: boolean;
  // From Electron's joystick:list. 'error' means the bridge exited on its own and can be relaunched.
  status?: 'running' | 'stopped' | 'queued' | 'error';
  error?: string;
}

// processStopped is observed child close from main, not a truthy IPC object
// and not a physical camera halt. physicalStopUnconfirmed means local
// repetition stopped but STOP publication did not complete (missing proxy
// or a throw) — not confirmed physical halt. savedRemoved is the delete
// event, savedAbsent the settled "no saved configuration" fact a retry can
// act on. hadRuntime is a live handle at stop time. outcomeUnknown means
// process/configuration facts are unknown; local stop publication is independent.
export type ControllerStopResult = {
  processStopped: boolean;
  physicalStopUnconfirmed: boolean;
  savedRemoved: boolean;
  savedAbsent: boolean;
  hadRuntime: boolean;
  outcomeUnknown: boolean;
};

const GATED_STOP_RESULT: ControllerStopResult = {
  processStopped: false,
  physicalStopUnconfirmed: false,
  savedRemoved: false,
  savedAbsent: false,
  hadRuntime: false,
  outcomeUnknown: false
};

// Settings finishes the delete UI when the saved configuration is gone and
// no live handle remains. An unconfirmed live child keeps the modal for
// retry, and so does a lost receipt — an unknown outcome is never treated
// as a completed delete.
// Per rule: absent-data-gates-permissively (ai.knowledge/global).
export function shouldSettleDeleteUi(result: ControllerStopResult): boolean {
  if (result.outcomeUnknown) return false;
  return result.savedAbsent && (result.processStopped || !result.hadRuntime);
}

// Compact IPC launch reply owned by this repo (ProcessInfo + historical pID).
type ViscaBridgeLaunchReply = {
  pid?: number;
  pID?: number | string;
  error?: string;
  generation?: number;
  existing?: boolean;
};

// Main owns child generation; replacing this record fences local admission independently.
type PortRecord = {
  generation: number | undefined;
  retired: boolean;
  connected: boolean;
};

class IPControllerManager {
  private proxy: T_Maybe<Proxy>;
  private eventEmitter = new EventEmitter();
  private currentJoystickListener: ((data: { port: number }) => void) | null = null;
  // One repeater and one device ID serve every bridge, because IP controllers
  // cannot be pinned to different cameras yet — they all drive the room's
  // selected camera. Last-controller delete nulls these shared fields;
  // survivors keep the Repeater instance. Deleting the port whose move is
  // repeating closes the interval immediately — not only when it is last.
  private repeater: T_Maybe<Repeater>;
  private currentDeviceID: T_Maybe<Device['id']>;
  // Operator room selection survives last-controller reset of currentDeviceID.
  // New port admission rewires through setDeviceID; leftover stdout cannot.
  private selectedDeviceID: T_Maybe<Device['id']>;
  // One lifecycle record per port: live generation, retirement, connection.
  private ports = new Map<number, PortRecord>();
  private inflightStops = new Map<number, Promise<ControllerStopResult>>();
  // Shared activity token: survivor move or new selection retires stale cleanup.
  private activityGeneration = 0;
  // Port whose last move is currently repeating. Connection count is not
  // the owner of that interval.
  private activeMovePort: number | undefined;
  private registryCommands: T_Maybe<DeviceRegistryCommand[]>;

  constructor() {
    if (isElectronApp()) {
      window?.electron?.ipcRenderer?.on('joystick:message', this.handleJoystickMessage.bind(this));
    }
  }

  /**
   * Handle incoming joystick messages. Optional generation is stamped by main.
   */
  private handleJoystickMessage(_event: unknown, message: string, generation?: number): void {
    try {
      const request = ViscaBridgeRequest.fromJSON(JSON.parse(message));
      if (!request) return;
      const port = parseInt(request.bridgeID);
      if (isNaN(port)) {
        console.error('>> Invalid bridgeID in joystick message:', request.bridgeID);
        return;
      }
      const record = this.ports.get(port);
      if (record?.retired) return;
      if (record?.generation != null && generation != null && generation !== record.generation)
        return;
      if (generation != null && record?.generation !== generation) {
        this.ports.set(port, { generation, retired: false, connected: record?.connected === true });
      }
      this.admitJoystickRequest(request, port);
    } catch (err) {
      console.error('>> Failed to parse joystick message:', err, message);
    }
  }

  private admitJoystickRequest(request: ViscaBridgeRequest, port: number): void {
    const record = this.ports.get(port);
    const isNewAdmission = !record?.connected;
    if (isNewAdmission && !this.currentDeviceID && this.selectedDeviceID) {
      void this.setDeviceID(this.selectedDeviceID);
    }
    this.checkConnection(port);

    if (request.command) {
      this.handleCommand(request.command);
    } else if (request.inquiry) {
      this.handleInquiry(request);
    } else if (request.move || request.moveStop) {
      this.repeater?.add(request);
      if (request.move) {
        this.activeMovePort = port;
        this.activityGeneration += 1;
      }
    }
  }

  /**
   * Handle command requests.
   */
  private handleCommand(command: ViscaBridgeCommand): void {
    const { key, values } = command;
    const devID = this.currentDeviceID;
    if (!devID) {
      console.error('>> No device ID found');
      return;
    }
    // Handle preset special cases
    if (handlePresetCommand(key, values, devID)) {
      return;
    }

    // Handle regular device settings commands
    const cmd = this.registryCommands?.find((c) => c.key === key);
    if (!cmd) {
      console.error(`>> Command Handling Error: No command definition found for key: ${key}`);
      return;
    }

    const settingToApply = buildSettingFromCommand(cmd, command);
    if (!settingToApply) {
      return;
    }

    const mutation = {
      control: { device: { [devID]: { deviceSettings: { [key]: settingToApply } } } }
    };

    const proxy = locateProxy(devID);
    if (!proxy) {
      console.error(`>> Command Handling Error: No proxy found for device ID: ${devID}`);
      return;
    }

    proxy.mutate(mutation, {}, true).catch((err: any) => {
      console.error(`>> Command Handling Error: Mutation failed for key ${key}:`, err);
    });
  }

  /**
   * Handle inquiry requests.
   */
  private handleInquiry(request: ViscaBridgeRequest): void {
    const devID = this.currentDeviceID;
    if (!devID) {
      console.error('>> No device ID found');
      return;
    }
    if (!request.inquiry) {
      console.error('>> No inquiry found');
      return;
    }
    const key = request.inquiry.key;
    // Already validated port.
    const port = parseInt(request.bridgeID);

    // Look for the key in the current settings.
    const commands = this.registryCommands;
    if (!commands) {
      console.error('>> No command definition found for key:', key);
      return;
    }

    const settings = locateProxy(devID)?.state.status?.device[devID]?.deviceSettings?.settings;
    if (!settings) {
      console.error('>> No settings found for device:', devID);
      return;
    }

    let responseBytes: T_Maybe<Uint8Array>;
    if (key === DeviceRegistryCommandKey.BLOCK_BEC_INQ) {
      responseBytes = processBlockInquiry(settings, commands);
    } else {
      responseBytes = processInquiry(settings, commands, key);
    }

    if (!responseBytes) {
      // TODO: Return error and handle on visca-bridge.
      console.error('>> No response bytes found for inquiry:', key);
      return;
    }

    // Send the response back to the electron app.
    const response = ViscaBridgeInquiryResponse.fromPartial({
      bridgeID: request.bridgeID,
      id: request.id,
      key: key,
      response: responseBytes!,
      error: undefined
    });

    ElectronAPI.respondViscaBridge(port, response);
    return;
  }

  // checkConnection updates the connection state for a given port.
  private checkConnection(port: number) {
    const record = this.ports.get(port);
    if (record?.connected) return;
    this.ports.set(port, { generation: record?.generation, retired: false, connected: true });
    this.eventEmitter.emit('joystickConnected', { port: port });
  }

  private connectedCount(): number {
    let count = 0;
    for (const record of this.ports.values()) {
      if (record.connected && !record.retired) count += 1;
    }
    return count;
  }

  // setDeviceID changes the current device for all the next joystick commands.
  async setDeviceID(deviceID: T_Maybe<Device['id']>) {
    if (!deviceID) {
      console.error('>> No device ID found');
      return;
    }
    this.selectedDeviceID = deviceID;
    if (this.currentDeviceID === deviceID) {
      return;
    }
    this.currentDeviceID = deviceID;
    this.activityGeneration += 1;

    // Set model config.
    const p = locateProxy(deviceID);

    if (!p) {
      console.error('>> No proxy found');
      return;
    }

    // Setup movement commands repeater.
    if (this.repeater) {
      this.repeater.close();
    }
    this.repeater = new Repeater(deviceID);

    const modelId = p.state.control?.studio?.device[deviceID]?.model;
    if (!modelId) {
      console.error('>> No model ID found');
      return;
    }

    const token = await ElectronAPI.getAccessToken();

    // This has a cache for the model config.
    fetchModelConfig(modelId, token)
      .then((config) => {
        if (!config || config.configurations.length !== 1) {
          console.error('>> No model config found');
          return;
        }
        this.registryCommands = config.configurations[0]?.commands;
      })
      .catch((error) => {
        console.error('>> Error fetching model config:', error);
      });
  }

  // Sends a request to the electron app to spawn a 'visca-bridge.exe' process.
  // Returns the spawned process id, or undefined when the surface cannot spawn one.
  //
  // ENG-4332: spawning the bridge is desktop-only. In the browser
  // `ElectronAPI.launchViscaBridge` resolves `undefined` and the throw below fired with
  // "No context or pid" — an error group for what is really "this needs the desktop
  // app". The browser path now records guidance and resolves `undefined`; the Electron
  // path keeps the throw, because there a missing pid IS a genuine spawn failure.
  async launchViscaBridge(
    localIP: string,
    port: number,
    name = '',
    controlledDeviceID = '',
    client: EnvelopeClient = resolveFeatureGateClient()
  ): Promise<string | undefined> {
    if (!isDesktopOnlyFeatureAvailable(client)) {
      reportDesktopOnlyFeatureGate(DESKTOP_ONLY_FEATURE.IP_CONTROLLER_MANAGEMENT, client);
      return undefined;
    }

    const context: IPControllerInfo = {
      name,
      localIP,
      port,
      controlledDeviceID
    };
    const prior = this.ports.get(port);
    const proc = (await ElectronAPI.launchViscaBridge(context)) as
      | ViscaBridgeLaunchReply
      | undefined;
    const pid = proc ? String(proc.pID ?? proc.pid ?? '') : '';
    if (!proc || proc.error || !pid || pid === '-1') {
      throw new Error(`>> Failed to launch Visca Bridge: ${proc?.error || 'No context or pid'}`);
    }
    const current = this.ports.get(port);
    // Concurrent delete or a newer admission supersedes this reply.
    if (current !== prior) {
      return pid;
    }
    // Rename / already-running must never un-retire a deleted port.
    if (proc.existing === true) {
      return pid;
    }
    this.ports.set(port, {
      generation: typeof proc.generation === 'number' ? proc.generation : undefined,
      retired: false,
      connected: current?.connected === true
    });
    return pid;
  }

  // Returns an array of currently running visca-bridge processes for the
  // electron that is associated with this studio.
  async getProcessesArray(): Promise<IPControllerInfo[]> {
    const proxy = this.localProxy();
    // If we are in an electron app, get the through IPC.
    if (isElectronApp()) {
      // Electron main owns the saved-controller store, so this list is the
      // authority: it already distinguishes saved-but-unavailable from
      // deleted. Mirroring it keeps a failed state-sync write retryable —
      // the next call recomputes the same removal from main's answer instead
      // of promoting the stale mirror to truth.
      const listed: IPControllerInfo[] = await ElectronAPI.listViscaBridges();
      if (!Array.isArray(listed)) throw new Error('Controller list could not be confirmed');
      for (const process of listed) {
        if (process.available === false) continue;
        if (typeof process.generation !== 'number') continue;
        const current = this.ports.get(process.port);
        if (current?.retired) continue;
        this.ports.set(process.port, {
          generation: process.generation,
          retired: false,
          connected: current?.connected === true
        });
      }

      saveControllers(listed, proxy).catch((error) => {
        console.error('>> Error saving controllers:', error);
      });
      return listed;
    }

    // If not in electron app, try to get the processes from state.
    const processes: IPControllerInfo[] = [];
    const devices = proxy.state.control?.studio?.device;
    if (!devices) {
      console.error('>> No devices found');
      return [];
    }
    for (const device of Object.values(devices)) {
      const parsed = this.parseControllerUri(device.control?.uri);
      if (!parsed) continue;
      processes.push({
        name: device.name ?? '',
        port: parsed.port,
        localIP: parsed.localIP
      });
    }
    return processes;
  }

  private parseControllerUri(
    uri: string | undefined
  ): { localIP: string; port: number } | undefined {
    if (!uri || !uri.startsWith(CONTROLLER_SCHEME)) return undefined;
    const ipPort = uri.slice(CONTROLLER_SCHEME.length).split(':');
    const localIP = ipPort[0];
    const portString = ipPort[1];
    if (!portString || !localIP) return undefined;
    const port = parseInt(portString);
    if (Number.isNaN(port)) return undefined;
    return { localIP, port };
  }

  // A reply without the outcome fields is not "nothing was running" — it is
  // no receipt at all. Returning undefined keeps that distinguishable from a
  // truthful all-false settlement.
  private readMainStop(raw: unknown):
    | {
        processCloseObserved: boolean;
        existed: boolean;
        savedRemoved: boolean;
        savedAbsent: boolean;
        generation?: number;
      }
    | undefined {
    if (!raw || typeof raw !== 'object' || !('processCloseObserved' in raw)) return undefined;
    const rec = raw as {
      processCloseObserved?: unknown;
      existed?: unknown;
      savedRemoved?: unknown;
      savedAbsent?: unknown;
      generation?: unknown;
    };
    if (
      typeof rec.processCloseObserved !== 'boolean' ||
      typeof rec.existed !== 'boolean' ||
      typeof rec.savedRemoved !== 'boolean' ||
      (typeof rec.savedAbsent !== 'boolean' && rec.savedRemoved !== true)
    )
      return undefined;
    return {
      processCloseObserved: rec.processCloseObserved === true,
      existed: rec.existed === true,
      savedRemoved: rec.savedRemoved === true,
      // Main reports the settled fact; a reply that only carries the delete
      // event still proves the configuration was gone when it answered.
      savedAbsent: rec.savedAbsent === true || rec.savedRemoved === true,
      ...(typeof rec.generation === 'number' ? { generation: rec.generation } : {})
    };
  }

  // Sends a request to the electron app to stop a 'visca-bridge.exe' process.
  // Always resolves a fielded result so UI callers can inspect it.
  //
  // ENG-4332: stopping the bridge is desktop-only. The browser has no process to stop,
  // so it records guidance and reports "not stopped" instead of silently claiming
  // success — a browser caller that believed `true` would leave the UI showing a
  // controller as removed while the desktop app still runs it.
  async stopProcess(
    port: number,
    client: EnvelopeClient = resolveFeatureGateClient()
  ): Promise<ControllerStopResult> {
    if (!isDesktopOnlyFeatureAvailable(client)) {
      reportDesktopOnlyFeatureGate(DESKTOP_ONLY_FEATURE.IP_CONTROLLER_MANAGEMENT, client);
      return { ...GATED_STOP_RESULT };
    }

    const inflight = this.inflightStops.get(port);
    if (inflight) return inflight;

    const op = this.stopProcessOwned(port);
    this.inflightStops.set(port, op);
    try {
      return await op;
    } finally {
      if (this.inflightStops.get(port) === op) this.inflightStops.delete(port);
    }
  }

  private async stopProcessOwned(port: number): Promise<ControllerStopResult> {
    const previous = this.ports.get(port);
    this.ports.set(port, {
      // Delete invalidates admission, but a retry still refers to the same main child.
      generation: previous?.generation,
      retired: true,
      connected: false
    });

    const capturedRepeater = this.repeater;
    const capturedDeviceID = this.currentDeviceID;
    const capturedActivity = this.activityGeneration;
    let physicalStopUnconfirmed = false;
    const ownsActiveMove = this.activeMovePort === port;
    const lastController = this.connectedCount() === 0;

    const haltCapturedRepeater = (): void => {
      try {
        const closeResult = capturedRepeater?.close();
        if (closeResult && closeResult.publishedStop === false) {
          physicalStopUnconfirmed = true;
        }
      } catch {
        physicalStopUnconfirmed = true;
      }
    };

    // Halt the repeating interval when this port owns it. Last-controller
    // only decides whether shared repeater/device fields are cleared.
    if (ownsActiveMove || lastController) {
      haltCapturedRepeater();
      if (ownsActiveMove && this.activeMovePort === port) this.activeMovePort = undefined;
    }
    if (lastController) {
      if (this.repeater === capturedRepeater) this.repeater = undefined;
      if (this.currentDeviceID === capturedDeviceID) this.currentDeviceID = undefined;
    }

    const finish = (): void => {
      if (this.repeater !== capturedRepeater) return;
      if (this.activityGeneration !== capturedActivity) return;
      if (this.connectedCount() !== 0) return;
      haltCapturedRepeater();
      if (this.repeater === capturedRepeater) this.repeater = undefined;
      if (this.currentDeviceID === capturedDeviceID) this.currentDeviceID = undefined;
    };

    let processStopped = false;
    let savedRemoved = false;
    let savedAbsent = false;
    let hadRuntime = false;
    let outcomeUnknown = false;
    try {
      const mainResult = this.readMainStop(await ElectronAPI.stopViscaBridge(port));
      if (!mainResult) {
        outcomeUnknown = true;
      } else {
        const expectedGen = previous?.generation;
        processStopped =
          mainResult.processCloseObserved === true &&
          (typeof mainResult.generation !== 'number' ||
            expectedGen === undefined ||
            mainResult.generation === expectedGen);
        savedRemoved = mainResult.savedRemoved;
        savedAbsent = mainResult.savedAbsent;
        hadRuntime = mainResult.existed;
      }
    } catch {
      // The local repetition halt above already happened, but nothing is
      // known about the process or the saved configuration.
      outcomeUnknown = true;
    } finally {
      finish();
    }
    return {
      processStopped,
      physicalStopUnconfirmed,
      savedRemoved,
      savedAbsent,
      hadRuntime,
      outcomeUnknown
    };
  }

  private localProxy() {
    // NOTE: In the future there might be multiple non-embedded proxies.
    if (this.proxy) {
      return this.proxy;
    }

    // Get the first non-embedded proxy.
    for (const prox of broker) {
      if (!prox.embedded) {
        this.proxy = prox;
        break;
      }
    }
    if (!this.proxy) {
      throw new Error('No proxy found');
    }
    return this.proxy;
  }

  public onJoystickConnected(listener: (data: { port: number }) => void) {
    // This ensures that only one listener is active at a time (React mount
    // and unmount behavior, might need a cleaner solution on React side if we
    // want multiple listeners).
    if (this.currentJoystickListener) {
      this.eventEmitter.off('joystickConnected', this.currentJoystickListener);
    }
    this.currentJoystickListener = listener;
    this.eventEmitter.on('joystickConnected', listener);
  }
}

export const ipControllerManager = new IPControllerManager();

// restoreIPControllers is called from outside the ipControllerManager
// (broker.ts:537) to restore the IP controllers when the app is started.
// Pre-ENG-3756 this called getProcessesArray() unconditionally, which on a
// fast snapshot load would race Electron main's controller-store load and
// destructively saveControllers([]) — wiping the user's saved controllers.
//
// Post-fix: we first await joystick:wait-ready (Electron main's readiness
// gate) and bail out on negative result OR renderer-timeout. Callers stay
// fire-and-forget — they don't await this Promise.
//
// Per rule: code-comments-required + reference-rules-in-comments
// (ai.knowledge/global) — cites direct precedent.
// See knowledge: use-canonical-logger-not-console (ai.knowledge/platform/
// studio.client) — `logger.warning` not `console.*`.
export async function restoreIPControllers() {
  // ENG-3756 browser-path guard: waitForJoystickReady() is an Electron-only
  // IPC (window.electron.ipcRenderer.invoke). In a browser studio client
  // window.electron is undefined, so the invoke returns undefined and the
  // Promise.race below resolves to `undefined` — `!result.ok` then throws a
  // TypeError on every web boot. The Electron readiness race only exists in
  // Electron, so in the browser we preserve the pre-fix behaviour and call
  // getProcessesArray() directly (a side-effect-free state read on that path;
  // the destructive saveControllers runs only in the Electron branch).
  if (!isElectronApp()) {
    try {
      await ipControllerManager.getProcessesArray();
    } catch (e) {
      logger.warning('IP controller restore failed', e);
    }
    return;
  }
  try {
    const result = await Promise.race([
      ElectronAPI.waitForJoystickReady(),
      // Renderer-side timeout — fires before main's INIT_DEADLINE_MS so the
      // negative path is the one we observe in the worst case.
      new Promise<{ ok: false }>((r) => setTimeout(() => r({ ok: false }), RENDERER_TIMEOUT_MS))
    ]);
    if (!result.ok) {
      // SAFE FALLBACK — state preserved, no destructive saveControllers([])
      // call. Main will catch up on the user's next list-trigger.
      logger.warning('IP controller restore skipped: Electron not ready in time');
      return;
    }
    await ipControllerManager.getProcessesArray();
  } catch (e) {
    logger.warning('IP controller restore failed', e);
  }
}
